import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Calendar, DownloadCloud, Loader2, Briefcase } from 'lucide-react';
import secureLocalStorage from "react-secure-storage";
import html2pdf from "html2pdf.js";
/**
 * Multiple Applied Job List Page — React component
 * - Fetches a list of applied jobs from an API (axios.post('/api/applications'))
 * - Displays each job card with download button, status badge, and smooth animations.
 * - Supports PDF download for each job (using /api/application/download endpoint).
 */

export default function AppliedJobListPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);
	const jobPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				user: secureLocalStorage.getItem("user"),
				token:secureLocalStorage.getItem("token"),
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/applied-job", payload);
			
			// Loop data (array)
			setJobs(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		jobPost();		
	}, []);
  
  async function handleDownload(job) {
    try {
		setDownloadingId(job.apply_id);
		const payload = {
			userId: import.meta.env.VITE_API_USER,
			password: import.meta.env.VITE_API_PASS,
			user: secureLocalStorage.getItem("user"),
			token:secureLocalStorage.getItem("token"),
			apply_id:job.apply_id,
		};
		const api = axios.create({
			baseURL: import.meta.env.VITE_API_BASE_URL,
			headers: { "Content-Type": "application/json" },
		});
		const res = await api.post("/application-download", payload);
		
				
		if(res?.data?.responseCode==="200"){
			const pdfUrl = res?.data?.pdf_url || res?.data?.url;
			if (!pdfUrl) throw new Error('No pdf_url in response');
			const fileResp = await axios.get(`${import.meta.env.VITE_API_SOURCE_URL}${pdfUrl}`, { responseType: 'blob' });
			const blob = new Blob([fileResp.data], { type: 'application/pdf' });
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement('a');
			a.href = url;
			a.download = `Application-Form-${job.apply_id}.pdf`;
			document.body.appendChild(a);
			a.click();
			a.remove();
			window.URL.revokeObjectURL(url);
			setDownloadingId(null);
		}
		
		setDownloadingId(null);
    } catch (e) {
      console.error(e);
      setError('Download failed.');
      setDownloadingId(null);
    }
  }
	const forceDownload = (url, filename) => {
		const link = document.createElement("a");
		link.href = url;
		link.setAttribute("download", filename);
		document.body.appendChild(link);
		link.click();
		link.remove();
	};
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white p-6">
      <div className="max-w-6xl mx-auto">
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center justify-between mb-6"
        >
          <h1 className="text-3xl font-bold text-slate-800 flex items-center gap-2">
            <Briefcase className="text-indigo-600" /> Applied Jobs
          </h1>
        </motion.header>

        {error && <div className="text-red-600 mb-4">{error}</div>}

        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="animate-spin" size={24} />
          </div>
        ) : jobs.length === 0 ? (
          <EmptyListSVG />
        ) : (
          <motion.ul layout className="grid gap-4">
            {jobs.map((job) => (
              <motion.li
                key={job.apply_id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4 }}
                className="bg-white rounded-xl p-5 shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-4"
              >
                <div>
                  <h2 className="text-lg font-semibold text-slate-800">{job.designation}</h2>
                  <p className="text-slate-500 text-sm">{job.department}</p>
				  <p className="text-slate-500 text-sm">Gender: {job.gender}, Job Type: {job.job_type}, Grade: {job.grade}</p>
                  <div className="mt-2 flex flex-wrap gap-3 text-sm text-slate-600">
                    <span className="flex items-center gap-1"><Calendar size={14}/>Applied: {formatDate(job.apply_date)}</span>
                    <span>Status: <span className={`font-medium ${getStatusColor(job.status)}`}>{job.status}</span></span>
                  </div>
                </div>

                <div className="flex gap-3 items-center">
                  <button
                    onClick={() => handleDownload(job)}
                    disabled={downloadingId === job.apply_id}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-indigo-600 to-cyan-500 text-white font-medium shadow hover:brightness-105 disabled:opacity-60"
                  >
                    {downloadingId === job.apply_id ? <Loader2 className="animate-spin" size={16}/> : <DownloadCloud size={16}/>}
                    {downloadingId === job.apply_id ? 'Preparing...' : 'Download'}
                  </button>
                </div>
              </motion.li>
            ))}
          </motion.ul>
        )}
      </div>
    </div>
  );
}

function getStatusColor(status) {
  switch (status) {
    case 'Pending': return 'text-yellow-600';
    case 'Approved': return 'text-green-600';
    case 'Rejected': return 'text-red-600';
    default: return 'text-slate-600';
  }
}

function formatDate(iso) {
  if (!iso) return '-';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch (e) {
    return iso;
  }
}

function EmptyListSVG() {
  return (
    <div className="bg-white rounded-xl p-10 text-center shadow-sm">
      <svg width="200" height="140" viewBox="0 0 200 140" className="mx-auto mb-4">
        <defs>
          <linearGradient id="emptyGradient" x1="0" x2="1">
            <stop offset="0%" stopColor="#6366f1" />
            <stop offset="100%" stopColor="#06b6d4" />
          </linearGradient>
        </defs>
        <rect x="20" y="30" width="160" height="80" rx="10" fill="url(#emptyGradient)" opacity="0.1" />
        <path d="M40 70h120" stroke="url(#emptyGradient)" strokeWidth="2" strokeDasharray="6 4" />
      </svg>
      <h3 className="text-lg font-semibold text-slate-800">No Applied Jobs Yet</h3>
      <p className="text-slate-500 mt-2">Once you apply for jobs, they’ll appear here for tracking and downloads.</p>
    </div>
  );
}
